import React from 'react';
import './LegalPage.css';

function TermsConditions() {
  return (
    <section className="legal-page">
      <div className="container">
        <div className="legal-content">
          <h1>Terms & Conditions</h1>
          <span className="legal-date">Last updated: March 31, 2026</span>

          <p>
            Please read these Terms and Conditions ("Terms") carefully before using the Budge mobile application
            ("App") operated by Budge ("we", "us", or "our"). By downloading, installing, or using the App, you
            agree to be bound by these Terms.
          </p>

          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using the App, you agree to be bound by these Terms. If you do not agree with any part
            of these Terms, you must not use the App.
          </p>

          <h2>2. Description of Service</h2>
          <p>Budge is a personal finance management application that allows users to:</p>
          <ul>
            <li>Track income and expenses</li>
            <li>Manage EMIs (Equated Monthly Installments) and loans</li>
            <li>Set and monitor monthly budgets</li>
            <li>Create and track savings goals</li>
            <li>View financial analytics and reports</li>
            <li>Export financial data as CSV</li>
          </ul>
          <p>
            Budge is a <strong>personal finance tracking tool</strong>. It does not provide financial advice,
            investment recommendations, or access to banking services.
          </p>

          <h2>3. User Accounts</h2>

          <h3>3.1 Registration</h3>
          <p>
            To use the App, you must create an account using a valid email address, Google account, or Apple ID.
            You must be at least 13 years old to create an account.
          </p>

          <h3>3.2 Account Security</h3>
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and for all activities
            that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
          </p>

          <h3>3.3 Account Accuracy</h3>
          <p>
            You agree to provide accurate and complete information when creating your account and to update such
            information to keep it accurate and current.
          </p>

          <h2>4. Subscription & Pricing</h2>

          <h3>4.1 Free Tier</h3>
          <p>
            Budge offers a free tier with core functionality including transaction tracking, budget management,
            EMI tracking, and basic analytics.
          </p>

          <h3>4.2 Pro Tier</h3>
          <p>
            The Pro tier is available at a subscription price (currently &#8377;149/month) and includes additional
            features. Pricing is subject to change with prior notice to subscribers.
          </p>

          <h3>4.3 Payments</h3>
          <p>
            Subscriptions are processed through the Apple App Store or Google Play Store. All payments are subject
            to the respective store's payment terms and refund policies.
          </p>

          <h3>4.4 Cancellation</h3>
          <p>
            You may cancel your Pro subscription at any time through the App Store or Google Play Store. Upon
            cancellation, you will retain Pro features until the end of the current billing period.
          </p>

          <h2>5. User Data & Content</h2>

          <h3>5.1 Your Data</h3>
          <p>
            You retain full ownership of any data you enter into the App (transactions, EMIs, budgets, goals, receipts).
            We do not claim ownership of your data.
          </p>

          <h3>5.2 Data Accuracy</h3>
          <p>
            You are solely responsible for the accuracy of the financial data you enter. Budge does not verify the
            accuracy of user-entered data and is not liable for any decisions made based on this data.
          </p>

          <h3>5.3 Data Deletion</h3>
          <p>
            You may delete your account and all associated data at any time. See our{' '}
            <a href="/delete-account">Account Deletion</a> page for instructions.
          </p>

          <h2>6. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the App for any unlawful purpose or in violation of any laws.</li>
            <li>Attempt to reverse engineer, decompile, or disassemble the App.</li>
            <li>Use the App to store or transmit malicious code.</li>
            <li>Attempt to gain unauthorized access to our systems or other users' accounts.</li>
            <li>Use automated methods (bots, scrapers) to access or interact with the App.</li>
            <li>Interfere with or disrupt the integrity of the App or its servers.</li>
          </ul>

          <h2>7. Intellectual Property</h2>
          <p>
            The App, including its design, code, features, graphics, and content, is the intellectual property of Budge
            and is protected by copyright and other intellectual property laws. You may not copy, modify, distribute, or
            create derivative works based on the App.
          </p>

          <h2>8. Disclaimers</h2>

          <h3>8.1 No Financial Advice</h3>
          <p>
            Budge is a personal finance <strong>tracking</strong> tool. The insights, analytics, and information provided
            by the App are for informational purposes only and do not constitute financial, investment, tax, or legal advice.
            Always consult qualified professionals for financial decisions.
          </p>

          <h3>8.2 Service Availability</h3>
          <p>
            We strive to keep the App available at all times but do not guarantee uninterrupted access. The App may be
            temporarily unavailable due to maintenance, updates, or circumstances beyond our control.
          </p>

          <h3>8.3 As-Is Basis</h3>
          <p>
            The App is provided on an "as-is" and "as-available" basis without warranties of any kind, either express or
            implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose,
            or non-infringement.
          </p>

          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by applicable law, Budge shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages, including but not limited to loss of data, loss of profits, or
            financial losses arising from:
          </p>
          <ul>
            <li>Your use of or inability to use the App.</li>
            <li>Any errors or inaccuracies in your financial data or the App's analytics.</li>
            <li>Unauthorized access to your account.</li>
            <li>Any third-party actions or services.</li>
          </ul>

          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Budge, its officers, directors, employees, and agents from any claims,
            damages, losses, liabilities, and expenses arising out of your use of the App or violation of these Terms.
          </p>

          <h2>11. Termination</h2>
          <p>
            We reserve the right to suspend or terminate your access to the App at any time, with or without cause, with
            or without notice. Upon termination, your right to use the App will immediately cease.
          </p>

          <h2>12. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of India, without regard to its
            conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction
            of the courts in India.
          </p>

          <h2>13. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of significant changes by updating
            the "Last updated" date and, where possible, through in-app notifications. Your continued use of the App after
            changes constitutes acceptance of the updated Terms.
          </p>

          <h2>14. Severability</h2>
          <p>
            If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall continue
            in full force and effect.
          </p>

          <h2>15. Contact Us</h2>
          <p>If you have any questions about these Terms, please contact us at:</p>
          <p><strong>Email:</strong> <a href="mailto:support@budgeapp.com">support@budgeapp.com</a></p>
        </div>
      </div>
    </section>
  );
}

export default TermsConditions;
